import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Box, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch } from '../../store/hooks';
import { addTeamMember, updateTeamMember, deleteTeamMember, TeamMember } from '../../store/slices/podsSlice';
import { removeMemberFromOverrides } from '../../store/slices/sprintsSlice';
import ModalActionFooter from '../ModalActionFooter/ModalActionFooter';

const GROUPS = ['Product', 'Design', 'Engineering'] as const;

const PRACTICES: Record<string, string[]> = {
  Product: ['Product Management', 'Product Owner', 'Business Analyst'],
  Design: ['UX Design', 'UI Design', 'Visual Design'],
  Engineering: ['Frontend', 'Backend', 'Full Stack', 'Quality', 'DevOps', 'Mobile'],
};

interface TeamMemberModalProps {
  open: boolean;
  podId: string | null;
  member: TeamMember | null;
  onClose: () => void;
}

const TeamMemberModal: React.FC<TeamMemberModalProps> = ({ open, podId, member, onClose }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [group, setGroup] = useState<'Product' | 'Design' | 'Engineering'>('Engineering');
  const [practice, setPractice] = useState('');

  useEffect(() => {
    if (member) {
      setName(member.name);
      setGroup(member.group);
      setPractice(member.practice);
    } else {
      setName('');
      setGroup('Engineering');
      setPractice('');
    }
  }, [member, open]);

  useEffect(() => {
    if (!member) setPractice(PRACTICES[group][0]);
  }, [group, member]);

  const handleSave = () => {
    if (!podId || !name.trim()) return;
    if (member) {
      dispatch(updateTeamMember({ podId, member: { ...member, name, group, practice } }));
    } else {
      dispatch(addTeamMember({ podId, member: { name, group, practice } }));
    }
    onClose();
  };

  const handleDelete = () => {
    if (!podId || !member) return;
    dispatch(deleteTeamMember({ podId, memberId: member.id }));
    dispatch(removeMemberFromOverrides(member.id));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{member ? 'Edit Team Member' : 'Add Team Member'}</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            select
            label="Group"
            value={group}
            onChange={(e) => setGroup(e.target.value as 'Product' | 'Design' | 'Engineering')}
            fullWidth
          >
            {GROUPS.map((g) => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Practice"
            value={practice}
            onChange={(e) => setPractice(e.target.value)}
            fullWidth
          >
            {PRACTICES[group].map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <ModalActionFooter
          onCancel={onClose}
          onSave={handleSave}
          saveLabel={member ? 'Save' : 'Add'}
          saveDisabled={!name.trim()}
          onDelete={member ? handleDelete : undefined}
          deleteLabel="Delete Member"
          deleteConfirmMessage="Are you sure you want to delete this team member? This action cannot be undone."
        />
      </DialogActions>
    </Dialog>
  );
};

export default TeamMemberModal;
